// Pure helpers for the Fleet Health page. No React, no imports, so they can be
// tested with plain `node` (see lib/__tests__/fleetHealth.test.mjs).
//
// Input is the list of Fleetio vehicle snapshots served by GET /api/fleetio/vehicles
// (shape built by backend/fleetio_sync.py: build_snapshots).

export const FILTERS = [
  { id: "all", label: "All units" },
  { id: "oos", label: "Out of service" },
  { id: "overdue", label: "Overdue" },
  { id: "faults", label: "Active faults" },
  { id: "issues", label: "Open issues" },
  { id: "due_soon", label: "Due soon" },
];

const len = (a) => (Array.isArray(a) ? a.length : 0);
const overdueCount = (v) => len(v.overdue_reminders) + (v.overdue_issue_count || 0);
const attentionCount = (v) => len(v.open_issues) + len(v.due_soon_reminders) + len(v.active_faults);

/** One of: "oos" | "critical" | "overdue" | "attention" | "ok". Worst condition wins. */
export function urgency(v) {
  if (v.out_of_service) return "oos";
  if (isStopLamp(v)) return "critical";
  if (overdueCount(v) > 0) return "overdue";
  if (attentionCount(v) > 0) return "attention";
  return "ok";
}

const RANK = { oos: 0, critical: 1, overdue: 2, attention: 3, ok: 4 };

/** Most urgent first; ties broken by how much is wrong, then by unit name (natural order). */
export function rankVehicles(vehicles) {
  return [...(vehicles || [])].sort((a, b) => {
    const ua = RANK[urgency(a)];
    const ub = RANK[urgency(b)];
    if (ua !== ub) return ua - ub;
    if (overdueCount(a) !== overdueCount(b)) return overdueCount(b) - overdueCount(a);
    if (attentionCount(a) !== attentionCount(b)) return attentionCount(b) - attentionCount(a);
    return String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true });
  });
}

/** Headline counts. "Units" counts vehicles; the rest count units except where noted. */
export function summarize(vehicles) {
  const s = {
    units: 0, outOfService: 0, overdueUnits: 0, overdueReminders: 0, overdueIssues: 0,
    openIssues: 0, faultUnits: 0, dueSoonUnits: 0, healthy: 0, matched: 0, unmatched: 0,
  };
  for (const v of vehicles || []) {
    s.units += 1;
    if (v.out_of_service) s.outOfService += 1;
    if (overdueCount(v) > 0) s.overdueUnits += 1;
    s.overdueReminders += len(v.overdue_reminders);
    s.overdueIssues += v.overdue_issue_count || 0;
    s.openIssues += len(v.open_issues);
    if (len(v.active_faults) > 0) s.faultUnits += 1;
    if (len(v.due_soon_reminders) > 0) s.dueSoonUnits += 1;
    if (urgency(v) === "ok") s.healthy += 1;
    if (v.asset_id) s.matched += 1; else s.unmatched += 1;
  }
  return s;
}

export function matchesFilter(v, id) {
  switch (id) {
    case "oos": return !!v.out_of_service;
    case "overdue": return overdueCount(v) > 0;
    case "issues": return len(v.open_issues) > 0;
    case "faults": return len(v.active_faults) > 0;
    case "due_soon": return len(v.due_soon_reminders) > 0;
    default: return true;
  }
}

/** Filter chip + free-text search over name, VIN, type and Fleetio status. */
export function filterVehicles(vehicles, filterId = "all", query = "") {
  const q = String(query || "").trim().toLowerCase();
  return (vehicles || []).filter((v) => {
    if (!matchesFilter(v, filterId)) return false;
    if (!q) return true;
    const codes = (v.active_faults || []).map((f) => f.code);
    return [v.name, v.vin, v.type, v.status_name, ...codes].some((f) => String(f || "").toLowerCase().includes(q));
  });
}

const DAY_MS = 86400000;

/**
 * "12 d overdue" / "due today" / "in 5 d" for an ISO date. Whole UTC days, so a date-only
 * value like "2026-10-20" never flips on the viewer's timezone. Unparseable/missing -> "".
 */
export function dueLabel(iso, now = Date.now()) {
  if (!iso) return { text: "", overdue: false, days: null };
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return { text: "", overdue: false, days: null };
  const days = Math.floor(t / DAY_MS) - Math.floor(now / DAY_MS);
  if (days < 0) return { text: `${-days} d overdue`, overdue: true, days };
  if (days === 0) return { text: "due today", overdue: false, days };
  return { text: `in ${days} d`, overdue: false, days };
}

/** Where a reminder is due: a date, a meter reading, or both. */
export function reminderDue(r, now = Date.now()) {
  const date = dueLabel(r && r.next_due_at, now);
  const parts = [];
  if (r && r.next_due_at) parts.push(String(r.next_due_at).slice(0, 10));
  if (r && r.next_due_meter != null) parts.push(`at ${Math.round(r.next_due_meter).toLocaleString("en-US")}`);
  return { when: parts.join(" · "), relative: date.text, overdue: date.overdue };
}

export function formatMeter(m) {
  if (!m || m.value == null) return "";
  const n = Math.round(m.value).toLocaleString("en-US");
  return m.unit ? `${n} ${m.unit}` : n;
}

/** "just now" / "5 min ago" / "3 h ago" / "2 d ago", or "never". */
export function ageLabel(iso, now = Date.now()) {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "never";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

/** Stale = no successful sync yet, or none within two sync intervals (default 15 min). */
export function isStale(status, now = Date.now()) {
  if (!status || !status.last_success_at) return true;
  const t = Date.parse(status.last_success_at);
  if (Number.isNaN(t)) return true;
  const interval = (status.sync_interval_s || 900) * 1000;
  return now - t > 2 * interval;
}

/** Lamps that are on, worst first: "stop" and "protect" are the serious ones. */
export function lampLabels(ce) {
  const order = [["stop", "STOP"], ["protect", "PROTECT"], ["warning", "WARNING"], ["emissions", "EMISSIONS"], ["mil", "CHECK ENGINE"]];
  return order.filter(([k]) => ce && ce[k]).map(([, label]) => label);
}

export const isStopLamp = (v) => !!(v.check_engine && (v.check_engine.stop || v.check_engine.protect));

/** Same key the backend uses for DieselOps lookups: "SPN 102 FMI 18", "SPN 102" or the OBD code. */
export function faultKey(f) {
  if (!f) return null;
  const has = (x) => x !== null && x !== undefined && String(x).trim() !== "";
  if (has(f.spn)) return has(f.fmi) ? `SPN ${Number(f.spn)} FMI ${Number(f.fmi)}` : `SPN ${Number(f.spn)}`;
  const code = String(f.code || "").trim().toUpperCase();
  return code && !code.startsWith("SPN") ? code : null;
}

/** Query body for POST /fleetio/fault-lookup (deduped by faultKey). */
export function lookupBody(faults) {
  const seen = new Set();
  const out = [];
  for (const f of faults || []) {
    const k = faultKey(f);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(f.spn !== null && f.spn !== undefined && String(f.spn).trim() !== ""
      ? { spn: Number(f.spn), ...(f.fmi !== null && f.fmi !== undefined && String(f.fmi).trim() !== "" ? { fmi: Number(f.fmi) } : {}) }
      : { code: k });
  }
  return { faults: out };
}

/** How to label a knowledge-base result. Never a safe/unsafe verdict. */
export function kbBadge(status) {
  switch (status) {
    case "kb_match": return { label: "IN KNOWLEDGE BASE", color: "#22c55e" };
    case "spn_related": return { label: "RELATED CODE ONLY", color: "#fbbf24" };
    default: return { label: "NOT IN KNOWLEDGE BASE", color: "#71717a" };
  }
}

export function severityColor(sev) {
  const s = String(sev || "").toUpperCase();
  if (s === "CRITICAL" || s === "HIGH") return "#ef4444";
  if (s === "MEDIUM" || s === "MODERATE") return "#f97316";
  if (s === "LOW") return "#fbbf24";
  return "#a1a1aa";
}
